import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatVND } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, PieChart, Pie, LineChart, Line,
} from "recharts";

let ApexChart = null;
try { ApexChart = require("react-apexcharts").default; } catch (e) { /* not available */ }
import {
  TrendingUp, TrendingDown, CalendarDays, Users, Target, Loader2,
  AlertTriangle, Clock, Sparkles, RefreshCw, BarChart3, Brain,
  ShieldAlert, Activity, Megaphone, Send, Check, X, Eye,
  MessageSquare, Phone, ArrowUpRight, CheckCircle2, Zap,
  Sun, Coffee, ChevronRight, Package, Store,
} from "lucide-react";

const LIME = "#84cc16";

/* ─────── shared helpers ─────── */
function CTooltip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f172a]/95 backdrop-blur border border-[#334155] rounded-lg px-3 py-2 shadow-xl text-xs">
      <p className="text-slate-400 mb-0.5">{label}</p>
      {payload.map((p, i) => <p key={i} className="text-slate-200 font-medium">{fmt ? fmt(p.value) : p.value}</p>)}
    </div>
  );
}

const RISK_CFG = {
  critical: { l: "Nguy hiểm", c: "text-red-400", bg: "bg-red-500/15", bc: "border-red-500/30", dot: "bg-red-400" },
  high: { l: "Cao", c: "text-orange-400", bg: "bg-orange-500/15", bc: "border-orange-500/30", dot: "bg-orange-400" },
  medium: { l: "Trung bình", c: "text-amber-400", bg: "bg-amber-500/15", bc: "border-amber-500/30", dot: "bg-amber-400" },
  low: { l: "Thấp", c: "text-green-400", bg: "bg-green-500/15", bc: "border-green-500/30", dot: "bg-green-400" },
};

const STATUS_CFG = {
  new: { l: "Mới", c: "text-sky-400", bg: "bg-sky-500/15" },
  sent: { l: "Đã gửi", c: "text-amber-400", bg: "bg-amber-500/15" },
  completed: { l: "Xong", c: "text-lime-400", bg: "bg-lime-500/15" },
  ignored: { l: "Bỏ qua", c: "text-slate-500", bg: "bg-slate-500/15" },
};

function ScoreBar({ score }) {
  const pct = Math.min(100, score);
  const bar = score >= 75 ? "bg-red-500" : score >= 50 ? "bg-orange-500" : score >= 30 ? "bg-amber-500" : "bg-green-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[#0f172a] rounded-full overflow-hidden"><div className={`h-full ${bar} rounded-full`} style={{ width: `${pct}%` }} /></div>
      <span className="text-[10px] font-mono font-bold text-slate-300 w-6 text-right">{score}</span>
    </div>
  );
}

/* ─────── HERO KPI STRIP — Valex Sale Card Style ─────── */
function HeroKPI({ icon: I, label, value, sub, gradient, sparkData }) {
  return (
    <div className={`relative overflow-hidden rounded-xl bg-gradient-to-b ${gradient} p-4 min-h-[130px] flex flex-col justify-between shadow-lg`}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/15">
          <I className="w-4 h-4 text-white" />
        </div>
        <span className="text-[10px] text-white/80 uppercase tracking-wider font-bold">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white tracking-tight">{value}</p>
      {sub && <p className="text-[10px] text-white/60">{sub}</p>}
      {/* Sparkline */}
      {ApexChart && sparkData && sparkData.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 opacity-35">
          <ApexChart type="area" height={45} width="100%"
            options={{
              chart: { sparkline: { enabled: true }, animations: { enabled: true, easing: 'easeinout', speed: 1000 } },
              stroke: { curve: 'smooth', width: 2 },
              fill: { type: 'gradient', gradient: { opacityFrom: 0.5, opacityTo: 0, stops: [0, 100] } },
              colors: ['#ffffff'],
              tooltip: { enabled: false },
            }}
            series={[{ data: sparkData }]}
          />
        </div>
      )}
    </div>
  );
}

/* ─────── MAIN COMPONENT ─────── */
export default function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("overview");
  const [period, setPeriod] = useState("month");

  // Detail dialogs
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailProfile, setDetailProfile] = useState(null);
  const [detailText, setDetailText] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);

  // Loading states
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [autoGen, setAutoGen] = useState(false);
  const [seedingAll, setSeedingAll] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [deletingSample, setDeletingSample] = useState(false);

  const seedAllData = async () => {
    if (!window.confirm("Tạo dữ liệu mẫu: 100 profiles, kho hàng, POS, chiến dịch?\n(Dữ liệu mẫu cũ sẽ bị xóa, dữ liệu thật KHÔNG bị ảnh hưởng)")) return;
    setSeedingAll(true);
    setSeedResult(null);
    try {
      const { data } = await api.post("/ai-engine/seed-all");
      setSeedResult(data);
      toast.success(data.message);
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: ["dash-overview"] });
      queryClient.invalidateQueries({ queryKey: ["morning-briefing"] });
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setSeedingAll(false); }
  };

  const deleteSampleData = async () => {
    if (!window.confirm("Xóa TẤT CẢ dữ liệu mẫu?\n(Dữ liệu thật KHÔNG bị ảnh hưởng)")) return;
    setDeletingSample(true);
    try {
      const { data } = await api.delete("/ai-engine/seed-all");
      toast.success(data.message);
      setSeedResult(null);
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: ["dash-overview"] });
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setDeletingSample(false); }
  };

  // Cron manual trigger
  const [cronRunning, setCronRunning] = useState(false);
  const runAllCron = async () => {
    setCronRunning(true);
    try {
      const { data } = await api.post("/cron/run-all");
      toast.success(`${data.message}: Churn ${data.churn.analyzed} profiles, sync ${data.churn.coffee_synced} Coffee, ${data.campaigns.created} campaigns, ${data.expiry.expiring} sắp hết hạn`);
      invalidateAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi chạy cron"); }
    finally { setCronRunning(false); }
  };

  // Morning briefing - lazy load only when button clicked
  const [briefingOpen, setBriefingOpen] = useState(false);
  const { data: briefingData, isLoading: briefingLoading, refetch: refetchBriefing } = useQuery({
    queryKey: ["morning-briefing"],
    queryFn: () => api.get("/ai-engine/morning-briefing").then(r => r.data),
    staleTime: 1000 * 60 * 30,
    enabled: false,
  });

  // Briefing: No auto-load. User clicks "Briefing" button to view.

  // Data queries
  const { data: overview, isLoading: ovLoading } = useQuery({
    queryKey: ["dash-overview", period],
    queryFn: () => api.get(`/dashboard/overview?period=${period}`).then(r => r.data),
  });
  const { data: heatmapData } = useQuery({
    queryKey: ["heatmap"], queryFn: () => api.get("/dashboard/hourly-heatmap").then(r => r.data),
  });
  const { data: churnData } = useQuery({
    queryKey: ["churn-dash"], queryFn: () => api.get("/ai-engine/churn-dashboard?limit=100").then(r => r.data),
    enabled: tab === "churn" || tab === "overview",
  });
  const { data: actionsData } = useQuery({
    queryKey: ["ai-actions-dash"], queryFn: () => api.get("/ai-engine/actions?limit=20").then(r => r.data),
    enabled: tab === "actions" || tab === "overview",
  });
  const { data: slotData } = useQuery({
    queryKey: ["slot-fill-dash"], queryFn: () => api.get("/ai-engine/slot-fill?days=7").then(r => r.data),
    enabled: tab === "slots" || tab === "overview",
  });
  const { data: forecastData } = useQuery({
    queryKey: ["forecast-dash"], queryFn: () => api.get("/ai-engine/revenue-forecast").then(r => r.data),
    enabled: tab === "forecast" || tab === "overview",
  });
  const { data: campaignsData } = useQuery({
    queryKey: ["campaigns-dash"], queryFn: () => api.get("/ai-engine/campaigns").then(r => r.data),
    enabled: tab === "campaigns" || tab === "overview",
  });
  const { data: analyticsData } = useQuery({
    queryKey: ["loyalty-analytics"], queryFn: () => api.get("/loyalty/analytics").then(r => r.data),
    enabled: tab === "overview",
  });
  const { data: optimizerData } = useQuery({
    queryKey: ["revenue-optimizer"], queryFn: () => api.get("/ai-engine/revenue-optimizer").then(r => r.data),
    enabled: tab === "overview",
  });
  const { data: invAlertsData } = useQuery({
    queryKey: ["inventory-alerts"], queryFn: () => api.get("/ai-engine/inventory-alerts").then(r => r.data),
    enabled: tab === "overview",
  });
  const { data: pricingData } = useQuery({
    queryKey: ["smart-pricing"], queryFn: () => api.get("/ai-engine/smart-pricing").then(r => r.data),
    enabled: tab === "overview",
  });

  const [seedingChurn, setSeedingChurn] = useState(false);
  const [restocking, setRestocking] = useState(false);
  const [rfmRunning, setRfmRunning] = useState(false);

  const seedChurn = async () => { setSeedingChurn(true); try { const { data } = await api.post("/ai-engine/seed-churn-data"); toast.success(data.message); await api.post("/ai-engine/run-churn-analysis"); invalidateAll(); } catch (e) { toast.error(e.response?.data?.detail || "Lỗi"); } finally { setSeedingChurn(false); } };
  const autoRestock = async () => { setRestocking(true); try { const { data } = await api.post("/ai-engine/auto-restock"); toast.success(data.message); } catch (e) { toast.error("Lỗi"); } finally { setRestocking(false); } };
  const runRFM = async () => { setRfmRunning(true); try { const { data } = await api.post("/loyalty/run-rfm"); toast.success(data.message); queryClient.invalidateQueries({ queryKey: ["loyalty-analytics"] }); } catch (e) { toast.error("Lỗi"); } finally { setRfmRunning(false); } };

  const kpi = overview?.kpi || {};
  const heatmap = heatmapData?.heatmap || [];
  const churnSummary = churnData?.summary || {};
  const churnProfiles = churnData?.profiles || [];
  const actions = actionsData?.actions || [];
  const actionsSummary = actionsData?.summary || {};
  const slots = slotData?.slots || [];
  const forecast = forecastData || {};
  const campaigns = campaignsData?.campaigns || [];
  const campSummary = campaignsData?.summary || {};
  const analytics = analyticsData || {};
  const optimizer = optimizerData?.actions || [];
  const invAlerts = invAlertsData?.alerts || [];
  const pricingSuggestions = pricingData?.suggestions || [];
  const churnCharts = churnData?.charts || {};

  const invalidateAll = () => {
    ["churn-dash", "ai-actions-dash", "campaigns-dash", "slot-fill-dash", "forecast-dash"].forEach(k =>
      queryClient.invalidateQueries({ queryKey: [k] })
    );
  };

  // Actions
  const runChurn = async () => { setAnalyzing(true); try { const { data } = await api.post("/ai-engine/run-churn-analysis"); toast.success(data.message); invalidateAll(); } catch (e) { toast.error("Lỗi"); } finally { setAnalyzing(false); } };
  const genActions = async () => { setGenerating(true); try { const { data } = await api.post("/ai-engine/generate-actions"); toast.success(data.message); invalidateAll(); } catch (e) { toast.error("Lỗi"); } finally { setGenerating(false); } };
  const autoGenCamp = async () => { setAutoGen(true); try { const { data } = await api.post("/ai-engine/campaigns/auto-generate"); toast.success(data.message); invalidateAll(); } catch (e) { toast.error("Lỗi"); } finally { setAutoGen(false); } };
  const approveCamp = async (id) => { try { await api.put(`/ai-engine/campaigns/${id}/approve`); toast.success("Đã duyệt"); invalidateAll(); } catch (e) { toast.error("Lỗi"); } };
  const sendCamp = async (id) => { try { const { data } = await api.post(`/ai-engine/campaigns/${id}/send`); toast.success(data.message); invalidateAll(); } catch (e) { toast.error("Lỗi"); } };
  const setActionStatus = async (id, status) => { try { await api.put(`/ai-engine/actions/${id}/status`, { status }); toast.success(`Đã cập nhật`); queryClient.invalidateQueries({ queryKey: ["ai-actions-dash"] }); } catch (e) { toast.error("Lỗi"); } };
  const viewChurnDetail = async (p) => { setDetailProfile(p); setDetailOpen(true); setDetailLoading(true); try { const { data } = await api.get(`/ai-engine/churn/${p.id}/detail`); setDetailText(data.llm_analysis); } catch { setDetailText(p.ai_explanation); } finally { setDetailLoading(false); } };
  const genMessage = async (a) => { setMsgOpen(true); setMsgLoading(true); setMsgText(a.recommended_message); try { const { data } = await api.post(`/ai-engine/actions/${a.id}/generate-message`); setMsgText(data.message); } catch {} finally { setMsgLoading(false); } };
  const createSlotCamp = async (s, ts) => { try { const { data } = await api.post("/ai-engine/slot-fill/create-campaign", { target_date: s.date, time_slot: `${ts.start}-${ts.end}`, discount: ts.suggested_discount, segment: s.recommended_segment }); toast.success(data.message); invalidateAll(); } catch (e) { toast.error("Lỗi"); } };

  if (ovLoading && !overview) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-lime-400" /></div>;
  }

  const peakData = (overview?.peak_hours || []).filter(h => h.avg_bookings > 0);
  const maxPeak = Math.max(...(peakData.map(p => p.avg_bookings) || [1]), 1);
  const highRisk = (churnSummary.critical || 0) + (churnSummary.high || 0);
  const emptySlots = slots.filter(s => s.empty_risk === "high").length;
  const pendingCamps = campSummary.draft || 0;
  const periodLabels = { week: "Tuần", month: "Tháng", year: "Năm" };

  const TABS = [
    { key: "overview", label: "Tổng quan", icon: BarChart3 },
    { key: "anomaly", label: "Bất thường", icon: AlertTriangle },
    { key: "churn", label: `Churn (${highRisk})`, icon: ShieldAlert },
    { key: "actions", label: `Hành động (${actionsSummary.new || 0})`, icon: Sparkles },
    { key: "slots", label: `Sân trống (${emptySlots})`, icon: Activity },
    { key: "forecast", label: "Dự báo", icon: TrendingUp },
    { key: "campaigns", label: `Chiến dịch (${pendingCamps})`, icon: Megaphone },
  ];

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="dashboard-page">
      {/* ══════ HEADER ══════ */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-lime-500/20 via-cyan-500/10 to-purple-500/10 flex items-center justify-center border border-lime-500/20 shadow-lg shadow-lime-500/5">
            <Brain className="w-5 h-5 text-lime-400" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-[#F8FAFC] tracking-tight">AI Command Center</h1>
            <p className="text-slate-500 text-[10px]">KARINOX Arena — {new Date().toLocaleDateString("vi-VN")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Morning Briefing button */}
          <Button size="sm" variant="outline" onClick={() => { refetchBriefing(); setBriefingOpen(true); }}
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-[10px] h-7 gap-1" data-testid="open-briefing-btn">
            <Sun className="w-3 h-3" /> Briefing
          </Button>
          {/* Seed data button */}
          <Button size="sm" onClick={seedAllData} disabled={seedingAll}
            className="bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] h-7 gap-1" data-testid="seed-all-btn">
            <Package className={`w-3 h-3 ${seedingAll ? "animate-spin" : ""}`} /> {seedingAll ? "Đang tạo..." : "Tạo dữ liệu mẫu"}
          </Button>
          <Button size="sm" variant="outline" onClick={deleteSampleData} disabled={deletingSample}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] h-7 gap-1" data-testid="delete-sample-btn">
            <X className={`w-3 h-3 ${deletingSample ? "animate-spin" : ""}`} /> {deletingSample ? "Đang xóa..." : "Xóa mẫu"}
          </Button>
          <Button size="sm" onClick={runAllCron} disabled={cronRunning}
            className="bg-purple-600 hover:bg-purple-500 text-white text-[10px] h-7 gap-1" data-testid="run-cron-btn">
            <Clock className={`w-3 h-3 ${cronRunning ? "animate-spin" : ""}`} /> {cronRunning ? "Đang chạy..." : "Chạy Cron"}
          </Button>
          {/* Period selector */}
          <div className="flex bg-[#0f172a] border border-[#334155] rounded-lg p-0.5">
            {["week", "month", "year"].map(p => (
              <button key={p} onClick={() => setPeriod(p)} data-testid={`period-${p}`}
                className={`px-3 py-1 rounded-md text-[10px] font-medium transition-all ${period === p ? "bg-lime-500/15 text-lime-400 shadow" : "text-slate-500 hover:text-slate-300"}`}>
                {periodLabels[p]}
              </button>
            ))}
          </div>
          {/* Quick actions */}
          {tab === "overview" && <Button size="sm" onClick={runRFM} disabled={rfmRunning} className="bg-cyan-500 hover:bg-cyan-400 text-[#0f172a] text-[10px] h-7 gap-1" data-testid="run-rfm-btn"><RefreshCw className={`w-3 h-3 ${rfmRunning ? "animate-spin" : ""}`} />{rfmRunning ? "..." : "Chạy RFM AI"}</Button>}
          {tab === "churn" && <Button size="sm" onClick={runChurn} disabled={analyzing} className="bg-red-500 hover:bg-red-400 text-white text-[10px] h-7 gap-1"><ShieldAlert className={`w-3 h-3 ${analyzing ? "animate-spin" : ""}`} />{analyzing ? "..." : "Phân tích"}</Button>}
          {tab === "actions" && <Button size="sm" onClick={genActions} disabled={generating} className="bg-amber-500 hover:bg-amber-400 text-[#0f172a] text-[10px] h-7 gap-1"><Sparkles className={`w-3 h-3 ${generating ? "animate-spin" : ""}`} />{generating ? "..." : "Tạo hành động"}</Button>}
          {tab === "campaigns" && <Button size="sm" onClick={autoGenCamp} disabled={autoGen} className="bg-purple-500 hover:bg-purple-400 text-white text-[10px] h-7 gap-1"><Megaphone className={`w-3 h-3 ${autoGen ? "animate-spin" : ""}`} />{autoGen ? "..." : "Auto tạo"}</Button>}
        </div>
      </div>

      {/* ══════ HERO KPI ══════ */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="hero-kpi">
        <HeroKPI icon={TrendingUp} label="DT Booking" value={formatVND(kpi.total_revenue || 0)} sub={`${kpi.revenue_change_percent > 0 ? "+" : ""}${kpi.revenue_change_percent || 0}% vs kỳ trước`} gradient="from-[#4680ff] to-[#5b8def]" sparkData={overview?.revenue_trend?.map(r => r.revenue) || []} />
        <HeroKPI icon={Store} label="DT KiotViet" value={formatVND(overview?.kiotviet?.revenue || 0)} sub={`${overview?.kiotviet?.invoice_count || 0} hóa đơn`} gradient="from-[#0162e8] to-[#4a6cf7]" sparkData={overview?.kiotviet?.trend?.map(t => t.revenue) || []} />
        <HeroKPI icon={CalendarDays} label="Booking" value={`${kpi.total_bookings || 0}`} sub="buổi" gradient="from-[#05c3a6] to-[#22d6b4]" sparkData={overview?.revenue_trend?.map(r => r.bookings || 1) || []} />
        <HeroKPI icon={ShieldAlert} label="Churn cao" value={`${highRisk}`} sub={`/ ${churnSummary.total_analyzed || 0} khách`} gradient="from-[#fc5286] to-[#fd6d8e]" sparkData={churnCharts?.trend || []} />
        <HeroKPI icon={Activity} label="San trong" value={`${emptySlots}`} sub="/ 7 ngày tới" gradient="from-[#fd7e14] to-[#ff9a44]" sparkData={slots.map(s => s.empty_slots || 0)} />
        <HeroKPI icon={Sparkles} label="Actions moi" value={`${actionsSummary.new || 0}`} sub="cần xử lý" gradient="from-[#7c3aed] to-[#a78bfa]" sparkData={actions.map((_, i) => i + 1)} />
        <HeroKPI icon={Megaphone} label="Campaign" value={`${pendingCamps}`} sub="chờ duyệt" gradient="from-[#0891b2] to-[#06b6d4]" sparkData={campaigns.map(c => c.recipients || 1)} />
      </div>

      {/* ══════ TABS ══════ */}
      <div className="flex gap-1 border-b border-[#334155] pb-px overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-xs font-medium rounded-t-lg transition-all flex items-center gap-1.5 whitespace-nowrap ${tab === t.key ? "bg-[#1e293b] text-[#F8FAFC] border border-[#334155] border-b-0 shadow-sm" : "text-slate-500 hover:text-slate-300"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════ TAB: OVERVIEW ══════════════ */}
      {tab === "overview" && (
        <div className="space-y-4">
          {/* Seed result banner */}
          {seedResult && (
            <div className="bg-lime-500/10 border border-lime-500/30 rounded-xl p-3 flex items-center gap-3" data-testid="seed-result-banner">
              <CheckCircle2 className="w-5 h-5 text-lime-400 shrink-0" />
              <div className="flex-1 text-xs text-lime-300">
                <b>Dữ liệu mẫu đã tạo:</b> {seedResult.profiles} profiles, {seedResult.transactions} giao dịch, {seedResult.actions} actions, {seedResult.inventory} sản phẩm kho, {seedResult.pos_orders} đơn POS, {seedResult.campaigns} chiến dịch
              </div>
              <button onClick={() => setSeedResult(null)} className="text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
            </div>
          )}
          {/* Revenue + Court */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="revenue-trend-chart">
              <h3 className="text-xs font-semibold text-[#F8FAFC] mb-2">Xu hướng doanh thu</h3>
              <div className="h-[180px]">
                {ApexChart && (overview?.revenue_trend || []).length > 0 ? (
                  <ApexChart type="area" height={180}
                    options={{
                      chart: { toolbar: { show: false }, animations: { enabled: true, easing: 'easeinout', speed: 800 } },
                      stroke: { curve: 'smooth', width: 2.5 },
                      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 95, 100] } },
                      colors: ['#84cc16', '#38bdf8'],
                      xaxis: { categories: (overview?.revenue_trend || []).map(d => d.date?.slice(5) || ""), labels: { style: { fontSize: '9px' } } },
                      yaxis: { labels: { formatter: v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : `${(v/1e3).toFixed(0)}K`, style: { fontSize: '9px' } } },
                      grid: { borderColor: '#334155', strokeDashArray: 3 },
                      legend: { position: 'bottom', fontSize: '10px', labels: { colors: ['#94a3b8'] } },
                      tooltip: { theme: 'dark', y: { formatter: v => formatVND(v) } },
                      dataLabels: { enabled: false },
                    }}
                    series={[
                      { name: 'Doanh thu', data: (overview?.revenue_trend || []).map(d => d.revenue) },
                      { name: 'Booking', data: (overview?.revenue_trend || []).map(d => (d.bookings || 0) * 100000) },
                    ]}
                  />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={overview?.revenue_trend || []}>
                      <defs><linearGradient id="rG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={LIME} stopOpacity={0.3} /><stop offset="95%" stopColor={LIME} stopOpacity={0} /></linearGradient></defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 9 }} tickFormatter={d => d?.slice(5) || ""} />
                      <YAxis tick={{ fill: "#64748b", fontSize: 9 }} tickFormatter={v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : `${(v/1e3).toFixed(0)}K`} />
                      <Tooltip content={<CTooltip fmt={v => formatVND(v)} />} />
                      <Area type="monotone" dataKey="revenue" stroke={LIME} fill="url(#rG)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="court-comparison-chart">
              <h3 className="text-xs font-semibold text-[#F8FAFC] mb-2">Doanh thu theo sân</h3>
              <div className="h-[180px]">
                {ApexChart && (overview?.court_comparison || []).length > 0 ? (
                  <ApexChart type="bar" height={180}
                    options={{
                      chart: { toolbar: { show: false }, animations: { enabled: true, easing: 'easeinout', speed: 600 } },
                      plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: '60%' } },
                      colors: ['#4680ff', '#22c03c', '#a78bfa', '#fb923c', '#fc5286', '#05c3a6'],
                      xaxis: { labels: { formatter: v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : `${(v/1e3).toFixed(0)}K`, style: { fontSize: '9px' } } },
                      yaxis: { labels: { style: { fontSize: '9px' } } },
                      grid: { borderColor: '#334155', strokeDashArray: 3 },
                      dataLabels: { enabled: false },
                      tooltip: { theme: 'dark', y: { formatter: v => formatVND(v) } },
                    }}
                    series={[{ name: 'Doanh thu', data: (overview?.court_comparison || []).map(c => ({ x: c.court_name, y: c.revenue })) }]}
                  />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={overview?.court_comparison || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis type="number" tick={{ fill: "#64748b", fontSize: 9 }} tickFormatter={v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : `${(v/1e3).toFixed(0)}K`} />
                      <YAxis dataKey="court_name" type="category" tick={{ fill: "#94a3b8", fontSize: 9 }} width={50} />
                      <Tooltip content={<CTooltip fmt={v => formatVND(v)} />} />
                      <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>{(overview?.court_comparison || []).map((_, i) => <Cell key={i} fill={["#84cc16","#38bdf8","#a78bfa","#fb923c","#f87171","#34d399"][i % 6]} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* AI Insights Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* RFM Segments mini */}
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <h3 className="text-xs font-semibold text-[#F8FAFC] mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-cyan-400" /> RFM Segments</h3>
              <div className="h-[170px]">
                {ApexChart && (analytics.segments || []).length > 0 ? (
                  <ApexChart type="bar" height={170}
                    options={{
                      chart: { toolbar: { show: false }, animations: { enabled: true, easing: 'easeinout', speed: 600 } },
                      plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: '55%' } },
                      colors: ['#06b6d4'],
                      xaxis: { labels: { style: { fontSize: '9px' } } },
                      yaxis: { labels: { style: { fontSize: '9px' } } },
                      grid: { borderColor: '#334155', strokeDashArray: 3 },
                      dataLabels: { enabled: false },
                      tooltip: { theme: 'dark' },
                    }}
                    series={[{ name: 'Khách', data: (analytics.segments || []).map(s => ({ x: s.segment, y: s.count })) }]}
                  />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.segments || []} layout="vertical">
                      <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 9 }} />
                      <YAxis type="category" dataKey="segment" tick={{ fill: "#e2e8f0", fontSize: 9 }} width={80} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 11 }} />
                      <Bar dataKey="count" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            {/* Forecast mini */}
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <h3 className="text-xs font-semibold text-[#F8FAFC] mb-2 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-lime-400" /> Dự báo 7 ngày</h3>
              <div className="text-xl font-bold text-lime-400 mb-1">{formatVND(forecast.forecast_7_days?.min || 0)} — {formatVND(forecast.forecast_7_days?.max || 0)}đ</div>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${forecast.trend === "up" ? "bg-lime-500/20 text-lime-400" : forecast.trend === "down" ? "bg-red-500/20 text-red-400" : "bg-slate-500/20 text-slate-400"}`}>
                  {forecast.trend === "up" ? `+${forecast.trend_pct}%` : forecast.trend === "down" ? `${forecast.trend_pct}%` : "Ổn định"}
                </span>
                <span className="text-[9px] text-slate-500">Tin cậy: {forecast.confidence || 0}%</span>
              </div>
              <div className="space-y-1">
                {(forecast.reasons || []).slice(0, 2).map((r, i) => <div key={i} className="text-[9px] text-slate-400 flex gap-1"><span className="text-lime-400">•</span>{r}</div>)}
              </div>
            </div>
            {/* Churn mini */}
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <h3 className="text-xs font-semibold text-[#F8FAFC] mb-2 flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5 text-red-400" /> Churn Risk</h3>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {[{ k: "critical", l: "Nguy hiểm", c: "text-red-400" }, { k: "high", l: "Cao", c: "text-orange-400" }, { k: "medium", l: "TB", c: "text-amber-400" }, { k: "low", l: "Thấp", c: "text-green-400" }].map(r => (
                  <div key={r.k} className="bg-[#0f172a] rounded-lg p-2 text-center">
                    <div className={`text-lg font-bold ${r.c}`}>{churnSummary[r.k] || 0}</div>
                    <div className="text-[8px] text-slate-500">{r.l}</div>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" className="w-full text-[10px] border-[#334155] text-slate-400 h-7" onClick={() => setTab("churn")}>Xem chi tiết</Button>
            </div>
          </div>

          {/* Peak Hours + Heatmap */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="peak-hours-chart">
              <h3 className="text-xs font-semibold text-[#F8FAFC] mb-2">Giờ cao điểm</h3>
              <div className="h-[160px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview?.peak_hours || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="hour" tick={{ fill: "#64748b", fontSize: 9 }} tickFormatter={h => parseInt(h)} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 9 }} />
                    <Tooltip content={<CTooltip fmt={v => `${v} booking`} />} />
                    <Bar dataKey="avg_bookings" radius={[3, 3, 0, 0]}>{(overview?.peak_hours || []).map((e, i) => <Cell key={i} fill={e.avg_bookings >= maxPeak * 0.8 ? LIME : e.avg_bookings > 0 ? "#38bdf8" : "#334155"} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* Alerts */}
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="alerts-section">
              <h3 className="text-xs font-semibold text-[#F8FAFC] mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Cảnh báo</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: overview?.alerts?.expired_members || 0, l: "Thành viên quá hạn", c: "text-red-400", bc: "border-red-500/20 bg-red-500/5", go: "/groups" },
                  { v: overview?.alerts?.expiring_members || 0, l: "Sắp hết hạn", c: "text-amber-400", bc: "border-amber-500/20 bg-amber-500/5", go: "/groups" },
                  { v: overview?.alerts?.pending_bookings || 0, l: "Chờ xác nhận", c: "text-orange-400", bc: "border-orange-500/20 bg-orange-500/5", go: "/" },
                  { v: overview?.alerts?.empty_courts_today || 0, l: "Sân trống hôm nay", c: "text-slate-400", bc: "border-slate-500/20 bg-slate-500/5" },
                ].map((a, i) => (
                  <button key={i} onClick={() => a.go && navigate(a.go)} className={`border rounded-lg p-2.5 text-left transition-all hover:scale-[1.02] ${a.bc}`}>
                    <p className={`text-xl font-bold ${a.c}`}>{a.v}</p>
                    <p className="text-[9px] text-slate-500">{a.l}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Top Customers */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden" data-testid="top-customers-table">
            <div className="px-4 py-3 border-b border-[#334155] flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-400" />
              <h3 className="text-xs font-semibold text-[#F8FAFC]">Top khách hàng</h3>
            </div>
            <div className="divide-y divide-[#334155]/50">
              {(overview?.top_customers || []).slice(0, 5).map((c, i) => (
                <div key={c.id || i} className="px-4 py-2.5 flex items-center gap-3 hover:bg-[#334155]/10 transition-colors" data-testid={`top-customer-${i}`}>
                  <span className="text-[10px] text-slate-500 w-5">{i + 1}</span>
                  <span className="text-xs font-medium text-slate-200 flex-1 truncate">{c.name}</span>
                  <span className="text-[10px] text-slate-500">{c.phone || "—"}</span>
                  <span className="text-xs font-bold text-lime-400 w-24 text-right">{formatVND(c.total_spent)}</span>
                  <span className="text-[10px] text-slate-500 w-12 text-right">{c.total_bookings} lần</span>
                </div>
              ))}
            </div>
          </div>

          {/* AI Quick Actions Panel */}
          <div className="bg-gradient-to-r from-[#1e293b] via-[#1e293b] to-[#0f172a] border border-lime-500/20 rounded-xl p-4 shadow-lg shadow-lime-500/5" data-testid="ai-quick-actions">
            <h3 className="text-xs font-bold text-[#F8FAFC] mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-lime-400" /> AI Quick Actions — Tối ưu doanh thu
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              {optimizer.slice(0, 5).map((a, i) => (
                <div key={i} className={`bg-[#0f172a] border rounded-lg p-3 ${a.priority <= 2 ? "border-red-500/20" : a.priority <= 3 ? "border-amber-500/20" : "border-[#334155]"}`} data-testid={`optimizer-action-${i}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`text-[8px] font-bold w-5 h-5 rounded-full flex items-center justify-center ${a.priority <= 2 ? "bg-red-500/20 text-red-400" : a.priority <= 3 ? "bg-amber-500/20 text-amber-400" : "bg-lime-500/20 text-lime-400"}`}>{a.priority}</span>
                    <span className="text-[10px] font-bold text-[#F8FAFC] line-clamp-1">{a.title}</span>
                  </div>
                  <p className="text-[9px] text-slate-400 line-clamp-2 mb-1">{a.description}</p>
                  {a.impact && <p className="text-[8px] text-lime-400 font-semibold">{a.impact}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Inventory Alerts + Smart Pricing mini */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <h3 className="text-xs font-bold text-[#F8FAFC] mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Cảnh báo kho ({invAlerts.length})</h3>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {invAlerts.slice(0, 5).map((a, i) => (
                  <div key={i} className={`flex items-center gap-2 bg-[#0f172a] rounded-lg px-2.5 py-2 text-[10px] ${a.severity === "critical" ? "border border-red-500/20" : "border border-[#334155]"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${a.severity === "critical" ? "bg-red-400" : "bg-amber-400"}`} />
                    <span className="text-slate-300 font-medium">{a.name}</span>
                    <span className="text-slate-600">×{a.stock}</span>
                    <span className="text-[9px] text-slate-500 ml-auto truncate max-w-[120px]">{a.action}</span>
                  </div>
                ))}
                {invAlerts.length === 0 && <p className="text-[10px] text-slate-500">Kho đầy đủ</p>}
              </div>
              <Button size="sm" variant="outline" className="w-full mt-2 text-[9px] border-[#334155] text-slate-400 h-6" onClick={autoRestock} disabled={restocking}>{restocking ? "Đang tạo..." : "AI Auto-Restock"}</Button>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <h3 className="text-xs font-bold text-[#F8FAFC] mb-2 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-cyan-400" /> AI Smart Pricing ({pricingSuggestions.length})</h3>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {pricingSuggestions.slice(0, 5).map((s, i) => (
                  <div key={i} className={`flex items-center gap-2 bg-[#0f172a] rounded-lg px-2.5 py-2 text-[10px] border ${s.action === "increase" ? "border-lime-500/20" : "border-red-500/20"}`}>
                    {s.action === "increase" ? <TrendingUp className="w-3 h-3 text-lime-400 shrink-0" /> : <TrendingDown className="w-3 h-3 text-red-400 shrink-0" />}
                    <span className="text-slate-300">{s.day} {s.label}</span>
                    <span className={`font-bold ${s.action === "increase" ? "text-lime-400" : "text-red-400"}`}>{s.action === "increase" ? "+" : "-"}{s.pct}%</span>
                    <span className="text-[8px] text-slate-600 ml-auto">{s.fill_rate}% lấp</span>
                  </div>
                ))}
                {pricingSuggestions.length === 0 && <p className="text-[10px] text-slate-500">Giá đang hợp lý</p>}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ══════════════ TAB: ANOMALY ══════════════ */}
      {tab === "anomaly" && <AnomalyTab />}

      {/* ══════════════ TAB: CHURN ══════════════ */}
      {tab === "churn" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[{ k: "critical", l: "Nguy hiểm", c: "text-red-400", bc: "border-red-500/30" }, { k: "high", l: "Cao", c: "text-orange-400", bc: "border-orange-500/30" }, { k: "medium", l: "Trung bình", c: "text-amber-400", bc: "border-amber-500/30" }, { k: "low", l: "Thấp", c: "text-green-400", bc: "border-green-500/30" }, { k: "", l: "Tổng", c: "text-[#F8FAFC]", bc: "border-[#334155]" }].map(s => (
              <div key={s.k || "all"} className={`bg-[#1e293b] border ${s.bc} rounded-xl p-3`}>
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">{s.l}</div>
                <div className={`text-2xl font-bold ${s.c} mt-1`}>{s.k ? (churnSummary[s.k] || 0) : (churnSummary.total_analyzed || 0)}</div>
              </div>
            ))}
          </div>
          {/* Churn Charts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="churn-score-dist">
              <h3 className="text-[11px] font-bold text-[#F8FAFC] mb-2">Phân bố Churn Score</h3>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={churnCharts.score_distribution || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="range" tick={{ fill: "#94a3b8", fontSize: 8 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 9 }} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 10 }} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>{(churnCharts.score_distribution || []).map((_, i) => <Cell key={i} fill={i >= 7 ? "#ef4444" : i >= 5 ? "#f59e0b" : i >= 3 ? "#eab308" : "#22c55e"} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="churn-risk-funnel">
              <h3 className="text-[11px] font-bold text-[#F8FAFC] mb-2">Phễu rủi ro</h3>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={churnCharts.risk_funnel || []} layout="vertical">
                  <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 9 }} />
                  <YAxis type="category" dataKey="level" tick={{ fill: "#e2e8f0", fontSize: 10 }} width={60} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 10 }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>{(churnCharts.risk_funnel || []).map((d, i) => <Cell key={i} fill={d.level === "critical" ? "#ef4444" : d.level === "high" ? "#f97316" : d.level === "medium" ? "#eab308" : "#22c55e"} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="churn-scatter">
              <h3 className="text-[11px] font-bold text-[#F8FAFC] mb-2">Churn vs Early Drop</h3>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={(churnCharts.early_drop_vs_churn || []).slice(0, 20)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 7 }} interval={0} angle={-45} textAnchor="end" height={40} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 9 }} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 10 }} />
                  <Bar dataKey="churn" fill="#ef4444" name="Churn" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="early_drop" fill="#f59e0b" name="Early Drop" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="churn-source-risk">
              <h3 className="text-[11px] font-bold text-[#F8FAFC] mb-2">Churn TB theo nguồn</h3>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={churnCharts.source_risk || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="source" tick={{ fill: "#94a3b8", fontSize: 9 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 9 }} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 10 }} />
                  <Bar dataKey="avg_churn" fill="#06b6d4" radius={[3, 3, 0, 0]} name="TB Churn" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="churn-tier-risk">
              <h3 className="text-[11px] font-bold text-[#F8FAFC] mb-2">Rủi ro theo hạng (stacked)</h3>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={churnCharts.tier_risk || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="tier" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 9 }} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 10 }} />
                  <Bar dataKey="critical" stackId="a" fill="#ef4444" name="Nguy hiểm" />
                  <Bar dataKey="high" stackId="a" fill="#f97316" name="Cao" />
                  <Bar dataKey="medium" stackId="a" fill="#eab308" name="TB" />
                  <Bar dataKey="low" stackId="a" fill="#22c55e" name="Thấp" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-slate-400">Danh sách khách hàng</h3>
            <Button size="sm" variant="outline" onClick={seedChurn} disabled={seedingChurn} className="text-[9px] border-[#334155] text-slate-500 h-6" data-testid="seed-churn-btn">{seedingChurn ? "Đang tạo..." : "Reset dữ liệu mẫu"}</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {churnProfiles.slice(0, 30).map(p => {
              const r = RISK_CFG[p.churn_risk_level] || RISK_CFG.low;
              return (
                <div key={p.id} className={`bg-[#1e293b] border ${r.bc} rounded-xl p-3.5`} data-testid={`churn-card-${p.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className={`w-2 h-2 rounded-full ${r.dot}`} />
                      <span className="text-xs font-bold text-[#F8FAFC] truncate">{p.name}</span>
                    </div>
                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${r.bg} ${r.c} font-semibold`}>{r.l}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <div><div className="text-[8px] text-slate-500">Churn</div><ScoreBar score={p.churn_score} /></div>
                    <div><div className="text-[8px] text-slate-500">Early Drop</div><ScoreBar score={p.early_drop_score} /></div>
                  </div>
                  <p className="text-[10px] text-amber-400/80 mb-1.5 line-clamp-2">{p.ai_explanation}</p>
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {p.churn_reasons?.slice(0, 2).map((rr, i) => <span key={i} className="text-[8px] bg-red-500/10 text-red-300 px-1 py-0.5 rounded">{rr}</span>)}
                  </div>
                  <Button size="sm" variant="outline" className="w-full text-[10px] border-[#334155] text-slate-400 h-6" onClick={() => viewChurnDetail(p)}><Eye className="w-3 h-3 mr-1" /> Chi tiết AI</Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══════════════ TAB: ACTIONS ══════════════ */}
      {tab === "actions" && (
        <div className="space-y-3">
          <div className="flex gap-3 text-[10px] text-slate-500">
            <span>Tổng: <b className="text-[#F8FAFC]">{actionsSummary.total || 0}</b></span>
            <span>Mới: <b className="text-sky-400">{actionsSummary.new || 0}</b></span>
            <span>Đã gửi: <b className="text-amber-400">{actionsSummary.sent || 0}</b></span>
            <span>Xong: <b className="text-lime-400">{actionsSummary.completed || 0}</b></span>
          </div>
          {actions.map(a => {
            const st = STATUS_CFG[a.action_status] || STATUS_CFG.new;
            return (
              <div key={a.id} className="bg-[#1e293b] border border-[#334155] rounded-xl p-3.5" data-testid={`action-card-${a.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2"><Target className={`w-3.5 h-3.5 ${a.priority === "high" ? "text-red-400" : a.priority === "medium" ? "text-amber-400" : "text-slate-400"}`} /><span className="text-xs font-bold text-[#F8FAFC]">{a.action_title}</span></div>
                  <div className="flex gap-1"><span className={`text-[8px] px-1.5 py-0.5 rounded ${a.priority === "high" ? "bg-red-500/15 text-red-400" : a.priority === "medium" ? "bg-amber-500/15 text-amber-400" : "bg-slate-500/15 text-slate-400"}`}>{a.priority}</span><span className={`text-[8px] px-1.5 py-0.5 rounded ${st.bg} ${st.c}`}>{st.l}</span></div>
                </div>
                <div className="text-[10px] text-slate-400 mb-1"><b className="text-[#F8FAFC]">{a.profile_name}</b> — {a.profile_phone}</div>
                <p className="text-[10px] text-slate-500 mb-1.5">{a.action_description}</p>
                <div className="bg-[#0f172a] rounded-lg p-2 mb-2 border border-[#334155]"><p className="text-[10px] text-slate-300 italic">"{a.recommended_message}"</p></div>
                <div className="flex gap-1">
                  <Button size="sm" className="text-[9px] bg-lime-500 hover:bg-lime-400 text-[#0f172a] h-6 px-2" onClick={() => genMessage(a)}><MessageSquare className="w-3 h-3 mr-0.5" />Tin nhắn</Button>
                  <Button size="sm" variant="outline" className="text-[9px] border-[#334155] text-amber-400 h-6 px-2" onClick={() => setActionStatus(a.id, "sent")}><Send className="w-3 h-3 mr-0.5" />Gửi</Button>
                  <Button size="sm" variant="outline" className="text-[9px] border-[#334155] text-green-400 h-6 px-2" onClick={() => setActionStatus(a.id, "completed")}><Check className="w-3 h-3 mr-0.5" />Xong</Button>
                  <Button size="sm" variant="ghost" className="text-[9px] text-slate-600 h-6 px-2" onClick={() => setActionStatus(a.id, "ignored")}><X className="w-3 h-3" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════ TAB: SLOT FILL ══════════════ */}
      {tab === "slots" && (
        <div className="space-y-4">
          {slots.map((s, i) => {
            const rCfg = RISK_CFG[s.empty_risk === "high" ? "critical" : s.empty_risk === "medium" ? "high" : "low"] || RISK_CFG.low;
            return (
              <div key={i} className={`bg-[#1e293b] border ${rCfg.bc} rounded-xl p-4`} data-testid={`slot-card-${s.date}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CalendarDays className={`w-4 h-4 ${rCfg.c}`} />
                    <span className="text-sm font-bold text-[#F8FAFC]">{s.day_of_week} {s.date}</span>
                    {s.is_weekend && <span className="text-[8px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">Cuối tuần</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>Lấp: <b className="text-[#F8FAFC]">{s.fill_rate}%</b></span>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full ${rCfg.bg} ${rCfg.c} font-semibold`}>{s.empty_risk === "high" ? "Nhiều trống" : s.empty_risk === "medium" ? "Trống TB" : "Ổn"}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                  {(s.time_slots || []).map((ts, j) => {
                    const tc = RISK_CFG[ts.empty_risk === "high" ? "critical" : ts.empty_risk === "medium" ? "high" : "low"];
                    return (
                      <div key={j} className={`bg-[#0f172a] border ${tc.bc} rounded-lg p-2`}>
                        <div className="flex justify-between mb-0.5"><span className="text-[9px] text-slate-300">{ts.label}</span><span className={`text-[8px] ${tc.c}`}>{ts.fill_rate}%</span></div>
                        <div className="text-[8px] text-slate-600 mb-1">{ts.start}-{ts.end}</div>
                        <div className="h-1 bg-[#1e293b] rounded-full overflow-hidden mb-1"><div className={`h-full rounded-full ${ts.fill_rate >= 70 ? "bg-lime-500" : ts.fill_rate >= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${ts.fill_rate}%` }} /></div>
                        {ts.empty_risk === "high" && ts.suggested_discount > 0 && (
                          <Button size="sm" variant="outline" className="w-full text-[8px] h-5 border-amber-500/30 text-amber-400 mt-1" onClick={() => createSlotCamp(s, ts)}>-{ts.suggested_discount}% campaign</Button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {s.suggestion && <p className="text-[10px] text-amber-400/80">{s.suggestion}</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════ TAB: FORECAST ══════════════ */}
      {tab === "forecast" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <h3 className="text-xs font-bold text-[#F8FAFC] mb-1">7 ngày tới</h3>
              <div className="text-2xl font-bold text-lime-400">{formatVND(forecast.forecast_7_days?.min || 0)} — {formatVND(forecast.forecast_7_days?.max || 0)}đ</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Đã book: {formatVND(forecast.forecast_7_days?.booked || 0)}đ</div>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <h3 className="text-xs font-bold text-[#F8FAFC] mb-1">30 ngày tới</h3>
              <div className="text-2xl font-bold text-amber-400">{formatVND(forecast.forecast_30_days?.min || 0)} — {formatVND(forecast.forecast_30_days?.max || 0)}đ</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Đã book: {formatVND(forecast.forecast_30_days?.booked || 0)}đ</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <h3 className="text-xs font-bold text-[#F8FAFC] mb-2">Nguồn doanh thu</h3>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={[
                    { name: "Sân", value: forecast.source_breakdown?.courts?.revenue || 0 },
                    { name: "POS/Cafe", value: forecast.source_breakdown?.pos_cafe?.revenue || 0 },
                    { name: "Hội viên", value: forecast.source_breakdown?.memberships?.revenue || 0 },
                  ].filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={3} dataKey="value"
                    label={({ name, value }) => `${name}: ${formatVND(value)}`}>
                    <Cell fill="#84cc16" /><Cell fill="#f59e0b" /><Cell fill="#06b6d4" />
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 10 }} formatter={v => formatVND(v) + "đ"} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xs font-bold text-[#F8FAFC]">Xu hướng</h3>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${forecast.trend === "up" ? "bg-lime-500/20 text-lime-400" : forecast.trend === "down" ? "bg-red-500/20 text-red-400" : "bg-slate-500/20 text-slate-400"}`}>
                  {forecast.trend === "up" ? `+${forecast.trend_pct}%` : forecast.trend === "down" ? `${forecast.trend_pct}%` : "Ổn định"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center mb-2">
                <div className="bg-[#0f172a] rounded-lg p-1.5"><div className="text-sm font-bold text-[#F8FAFC]">{formatVND(forecast.historical?.recent_30d || 0)}đ</div><div className="text-[7px] text-slate-500">30 ngày qua</div></div>
                <div className="bg-[#0f172a] rounded-lg p-1.5"><div className="text-sm font-bold text-slate-400">{formatVND(forecast.historical?.prev_30d || 0)}đ</div><div className="text-[7px] text-slate-500">Trước đó</div></div>
                <div className="bg-[#0f172a] rounded-lg p-1.5"><div className="text-sm font-bold text-lime-400">{formatVND(forecast.historical?.daily_avg || 0)}đ</div><div className="text-[7px] text-slate-500">TB/ngày</div></div>
              </div>
              <div className="space-y-0.5">{(forecast.reasons || []).map((r, i) => <div key={i} className="text-[9px] text-slate-400 flex gap-1"><span className="text-lime-400">•</span>{r}</div>)}</div>
              {forecast.alert && <div className="mt-2 bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-[10px] text-red-400 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{forecast.alert}</div>}
            </div>
          </div>
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <h3 className="text-xs font-bold text-[#F8FAFC] mb-2">Doanh thu 30 ngày + Dự báo</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={[...(forecast.chart_data || []).map(d => ({ ...d, t: "a" })), ...(forecast.forecast_chart || []).map(d => ({ date: d.date, revenue: d.forecast, t: "f" }))]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 8 }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 8 }} tickFormatter={v => `${(v / 1e6).toFixed(1)}M`} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 10 }} formatter={v => formatVND(v) + "đ"} />
                <Bar dataKey="revenue" radius={[2, 2, 0, 0]}>{[...(forecast.chart_data || []), ...(forecast.forecast_chart || [])].map((_, i) => <Cell key={i} fill={i >= (forecast.chart_data || []).length ? "#84cc1660" : LIME} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ══════════════ TAB: CAMPAIGNS ══════════════ */}
      {tab === "campaigns" && (
        <div className="space-y-3">
          <div className="flex gap-3 text-[10px] text-slate-500">
            <span>Draft: <b className="text-sky-400">{campSummary.draft || 0}</b></span>
            <span>Duyệt: <b className="text-lime-400">{campSummary.approved || 0}</b></span>
            <span>Gửi: <b className="text-amber-400">{campSummary.sent || 0}</b></span>
          </div>
          {campaigns.length === 0 ? (
            <div className="text-center py-12 text-slate-500"><Megaphone className="w-10 h-10 mx-auto mb-3 text-slate-600" /><p>Chưa có. Nhấn "Auto tạo" để bắt đầu.</p></div>
          ) : campaigns.map(c => {
            const typeL = { slot_fill: "Lấp sân", churn_reactivation: "Kích hoạt", membership_renewal: "Gia hạn", weekly_promo: "Promo" };
            const stC = { draft: { l: "Draft", c: "text-sky-400", bg: "bg-sky-500/15" }, approved: { l: "Đã duyệt", c: "text-lime-400", bg: "bg-lime-500/15" }, sent: { l: "Đã gửi", c: "text-amber-400", bg: "bg-amber-500/15" } };
            const st = stC[c.status] || stC.draft;
            return (
              <div key={c.id} className="bg-[#1e293b] border border-[#334155] rounded-xl p-3.5" data-testid={`campaign-${c.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2"><Megaphone className="w-3.5 h-3.5 text-purple-400" /><span className="text-xs font-bold text-[#F8FAFC]">{c.name}</span><span className="text-[8px] px-1 py-0.5 rounded bg-purple-500/15 text-purple-400">{typeL[c.campaign_type] || c.campaign_type}</span></div>
                  <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${st.bg} ${st.c}`}>{st.l}</span>
                </div>
                <div className="flex gap-3 text-[9px] text-slate-500 mb-1.5">{c.target_date && <span>Ngày: {c.target_date}</span>}{c.discount_percent > 0 && <span>Giảm: {c.discount_percent}%</span>}<span>Đối tượng: {c.target_count || "Tất cả"}</span>{c.sent_count > 0 && <span>Gửi: {c.sent_count}</span>}</div>
                <div className="bg-[#0f172a] rounded-lg p-2 mb-2 border border-[#334155]"><p className="text-[10px] text-slate-300 italic">"{c.message_template}"</p></div>
                <div className="flex gap-1.5">
                  {c.status === "draft" && <Button size="sm" className="text-[9px] bg-lime-500 hover:bg-lime-400 text-[#0f172a] h-6 px-2" onClick={() => approveCamp(c.id)} data-testid={`approve-${c.id}`}><CheckCircle2 className="w-3 h-3 mr-0.5" />Duyệt</Button>}
                  {c.status === "approved" && <Button size="sm" className="text-[9px] bg-amber-500 hover:bg-amber-400 text-[#0f172a] h-6 px-2" onClick={() => sendCamp(c.id)} data-testid={`send-${c.id}`}><Send className="w-3 h-3 mr-0.5" />Gửi (MOCK)</Button>}
                  {c.status === "sent" && <span className="text-[9px] text-lime-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Đã gửi {c.sent_count}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ Dialogs ═══ */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2"><Brain className="w-5 h-5 text-cyan-400" />Phân tích AI: {detailProfile?.name}</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">Churn: {detailProfile?.churn_score}/100 | Early Drop: {detailProfile?.early_drop_score}/100</DialogDescription>
          </DialogHeader>
          <div className="py-2">{detailLoading ? <div className="flex items-center gap-2 text-slate-400 text-sm"><RefreshCw className="w-4 h-4 animate-spin" />Đang phân tích...</div> : <div className="bg-[#0f172a] rounded-lg p-4 border border-[#334155] text-sm text-slate-300 whitespace-pre-wrap">{detailText}</div>}</div>
          <DialogFooter><Button variant="ghost" onClick={() => setDetailOpen(false)} className="text-slate-400">Đóng</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md">
          <DialogHeader><DialogTitle className="text-[#F8FAFC] flex items-center gap-2"><MessageSquare className="w-5 h-5 text-lime-400" />Tin nhắn cá nhân hóa</DialogTitle></DialogHeader>
          <div className="py-2">{msgLoading ? <div className="flex items-center gap-2 text-slate-400 text-sm"><RefreshCw className="w-4 h-4 animate-spin" />AI đang tạo...</div> : <div className="bg-[#0f172a] rounded-lg p-4 border border-[#334155] text-sm text-slate-300 whitespace-pre-wrap">{msgText}</div>}</div>
          <DialogFooter><Button variant="ghost" onClick={() => setMsgOpen(false)} className="text-slate-400">Đóng</Button><Button className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-bold gap-1.5" onClick={() => { navigator.clipboard.writeText(msgText); toast.success("Đã copy!"); }}>Copy</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ Morning Briefing Dialog ═══ */}
      <Dialog open={briefingOpen} onOpenChange={setBriefingOpen}>
        <DialogContent className="bg-gradient-to-b from-[#1e293b] to-[#0f172a] border-lime-500/20 sm:max-w-lg shadow-2xl shadow-lime-500/5" data-testid="morning-briefing-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2.5 text-base">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/30 to-orange-500/20 flex items-center justify-center border border-amber-500/30">
                <Sun className="w-5 h-5 text-amber-400" />
              </div>
              CEO Morning Briefing
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">{briefingData?.date || ""}</DialogDescription>
          </DialogHeader>
          {briefingData ? (
            <div className="space-y-3 py-1 max-h-[60vh] overflow-y-auto">
              {/* AI Summary */}
              <div className="bg-[#0f172a] border border-lime-500/20 rounded-xl p-3.5">
                <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap" data-testid="briefing-ai-summary">{briefingData.ai_summary}</p>
              </div>

              {/* Yesterday + Today side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#0f172a] border border-[#334155] rounded-xl p-3">
                  <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Coffee className="w-3 h-3" /> Hôm qua</div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between"><span className="text-[10px] text-slate-400">Booking</span><span className="text-xs font-bold text-[#F8FAFC]">{briefingData.yesterday?.bookings || 0}</span></div>
                    <div className="flex justify-between"><span className="text-[10px] text-slate-400">Doanh thu sân</span><span className="text-xs font-bold text-lime-400">{formatVND(briefingData.yesterday?.revenue || 0)}đ</span></div>
                    <div className="flex justify-between"><span className="text-[10px] text-slate-400">POS</span><span className="text-xs font-bold text-amber-400">{formatVND(briefingData.yesterday?.pos_revenue || 0)}đ</span></div>
                    <div className="flex justify-between border-t border-[#334155] pt-1"><span className="text-[10px] text-slate-400">Tổng</span><span className="text-xs font-bold text-[#F8FAFC]">{formatVND(briefingData.yesterday?.total_revenue || 0)}đ</span></div>
                    <div className="flex items-center gap-1 text-[10px]">
                      {(briefingData.yesterday?.revenue_change || 0) >= 0 ? <TrendingUp className="w-3 h-3 text-lime-400" /> : <TrendingDown className="w-3 h-3 text-red-400" />}
                      <span className={(briefingData.yesterday?.revenue_change || 0) >= 0 ? "text-lime-400" : "text-red-400"}>{briefingData.yesterday?.revenue_change > 0 ? "+" : ""}{briefingData.yesterday?.revenue_change || 0}%</span>
                      <span className="text-slate-600">vs hôm trước</span>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0f172a] border border-[#334155] rounded-xl p-3">
                  <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Sun className="w-3 h-3 text-amber-400" /> Hôm nay</div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between"><span className="text-[10px] text-slate-400">Đã đặt</span><span className="text-xs font-bold text-[#F8FAFC]">{briefingData.today?.bookings_scheduled || 0} booking</span></div>
                    <div className="flex justify-between"><span className="text-[10px] text-slate-400">Lấp sân</span><span className="text-xs font-bold text-sky-400">{briefingData.today?.fill_rate || 0}%</span></div>
                    <div className="flex justify-between"><span className="text-[10px] text-slate-400">Peak</span><span className="text-[10px] text-amber-400">{briefingData.today?.peak_slot || "—"}</span></div>
                    <div className="flex justify-between"><span className="text-[10px] text-slate-400">Slot trống</span><span className="text-xs font-bold text-red-400">{briefingData.today?.empty_slots?.length || 0}</span></div>
                  </div>
                </div>
              </div>

              {/* Alerts */}
              {(briefingData.alerts || []).length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[9px] text-slate-500 uppercase tracking-wider flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-400" /> Cảnh báo</div>
                  {briefingData.alerts.map((a, i) => (
                    <div key={i} className={`flex items-start gap-2 rounded-lg px-3 py-2 text-[10px] ${a.severity === "high" ? "bg-red-500/10 border border-red-500/20 text-red-300" : a.severity === "warning" ? "bg-amber-500/10 border border-amber-500/20 text-amber-300" : "bg-[#0f172a] border border-[#334155] text-slate-400"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full mt-1 shrink-0 ${a.severity === "high" ? "bg-red-400" : a.severity === "warning" ? "bg-amber-400" : "bg-slate-500"}`} />
                      {a.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Key Actions */}
              <div className="space-y-1.5">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider flex items-center gap-1"><Zap className="w-3 h-3 text-lime-400" /> Hành động ưu tiên</div>
                {(briefingData.key_actions || []).map((a, i) => (
                  <div key={i} className="flex items-center gap-2 bg-lime-500/5 border border-lime-500/15 rounded-lg px-3 py-2 text-[10px] text-lime-300">
                    <ChevronRight className="w-3 h-3 shrink-0" /> {a}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-lime-400 mx-auto mb-2" /><p className="text-xs text-slate-500">Đang tạo briefing...</p></div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBriefingOpen(false)} className="text-slate-400 text-xs">Đóng</Button>
            <Button onClick={() => setBriefingOpen(false)} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-bold text-xs gap-1.5" data-testid="briefing-start-btn">
              <Zap className="w-3.5 h-3.5" /> Bắt đầu ngày mới
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ══════════════ ANOMALY TAB ══════════════
function AnomalyTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterModule, setFilterModule] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [recs, setRecs] = useState({});
  const [recLoading, setRecLoading] = useState({});

  const scan = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/anomaly/scan");
      setData(res);
    } catch { toast.error("Lỗi scan bất thường"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { scan(); }, [scan]);

  const getRecommendation = async (alert, idx) => {
    setRecLoading(prev => ({ ...prev, [idx]: true }));
    try {
      const { data: res } = await api.post("/anomaly/recommend", {
        module: alert.module,
        severity: alert.severity,
        title: alert.title,
        detail: alert.detail,
        value: alert.value || 0,
        threshold: alert.threshold || 0,
      });
      setRecs(prev => ({ ...prev, [idx]: res.recommendation }));
    } catch { toast.error("Lỗi tạo gợi ý AI"); }
    finally { setRecLoading(prev => ({ ...prev, [idx]: false })); }
  };

  const MODULE_INFO = {
    ingredient: { label: "Nguyên liệu", color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/20" },
    revenue: { label: "Doanh thu", color: "text-lime-400", bg: "bg-lime-500/10 border-lime-500/20" },
    booking: { label: "Booking", color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20" },
    pos: { label: "POS", color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/20" },
    customer: { label: "Khách hàng", color: "text-teal-400", bg: "bg-teal-500/10 border-teal-500/20" },
    cashbook: { label: "Sổ quỹ", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
    staff: { label: "Nhân viên", color: "text-pink-400", bg: "bg-pink-500/10 border-pink-500/20" },
  };

  const SEVERITY_CLS = {
    high: { label: "Nghiêm trọng", dot: "bg-red-400", bg: "bg-red-500/10 border-red-500/30", text: "text-red-400" },
    medium: { label: "Cảnh báo", dot: "bg-amber-400", bg: "bg-amber-500/10 border-amber-500/30", text: "text-amber-400" },
    low: { label: "Lưu ý", dot: "bg-sky-400", bg: "bg-sky-500/10 border-sky-500/30", text: "text-sky-400" },
  };

  const alerts = (data?.alerts || []).filter(a => {
    if (filterModule && a.module !== filterModule) return false;
    if (filterSeverity && a.severity !== filterSeverity) return false;
    return true;
  });

  const summary = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="anomaly-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-[#F8FAFC]">{summary.total || 0}</div>
          <div className="text-[10px] text-slate-500 uppercase mt-1">Tổng cảnh báo</div>
        </div>
        <div className="bg-[#1e293b] border border-red-500/20 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-red-400">{summary.high || 0}</div>
          <div className="text-[10px] text-red-400/60 uppercase mt-1">Nghiêm trọng</div>
        </div>
        <div className="bg-[#1e293b] border border-amber-500/20 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-amber-400">{summary.medium || 0}</div>
          <div className="text-[10px] text-amber-400/60 uppercase mt-1">Cảnh báo</div>
        </div>
        <div className="bg-[#1e293b] border border-sky-500/20 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-sky-400">{summary.low || 0}</div>
          <div className="text-[10px] text-sky-400/60 uppercase mt-1">Lưu ý</div>
        </div>
      </div>

      {/* Filters + Rescan */}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={filterModule} onChange={e => setFilterModule(e.target.value)}
          className="bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-1.5 text-xs text-slate-200">
          <option value="">Tất cả module</option>
          {Object.entries(MODULE_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
          className="bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-1.5 text-xs text-slate-200">
          <option value="">Tất cả mức độ</option>
          <option value="high">Nghiêm trọng</option>
          <option value="medium">Cảnh báo</option>
          <option value="low">Lưu ý</option>
        </select>
        <span className="text-xs text-slate-500 ml-auto">{alerts.length} cảnh báo</span>
        <Button size="sm" onClick={scan} disabled={loading}
          className="bg-lime-500 text-[#0f172a] font-semibold text-xs" data-testid="rescan-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Quét lại
        </Button>
      </div>

      {/* Alert cards */}
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-lime-400 mx-auto mb-2" /><p className="text-xs text-slate-500">Đang quét 7 modules...</p></div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-12 bg-lime-500/5 border border-lime-500/20 rounded-xl">
          <CheckCircle2 className="w-10 h-10 text-lime-400 mx-auto mb-2" />
          <p className="text-sm text-lime-400 font-semibold">Không phát hiện bất thường</p>
          <p className="text-xs text-slate-500 mt-1">Hệ thống đang hoạt động bình thường</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((alert, idx) => {
            const mod = MODULE_INFO[alert.module] || MODULE_INFO.revenue;
            const sev = SEVERITY_CLS[alert.severity] || SEVERITY_CLS.low;
            const rec = recs[idx];
            const isRecLoading = recLoading[idx];
            return (
              <div key={idx} className={`${sev.bg} border rounded-xl p-4`} data-testid={`alert-${idx}`}>
                <div className="flex items-start gap-3">
                  <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${sev.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs font-bold ${sev.text}`}>{alert.title}</span>
                      <Badge className={`${mod.bg} ${mod.color} text-[9px] border`}>{mod.label}</Badge>
                      <Badge className={`${sev.bg} ${sev.text} text-[9px] border`}>{sev.label}</Badge>
                    </div>
                    <p className="text-xs text-slate-300">{alert.detail}</p>
                    <div className="flex items-center gap-3 mt-2">
                      {alert.value !== undefined && (
                        <div className="flex items-center gap-3 text-[10px] text-slate-500">
                          <span>Giá trị: <strong className={sev.text}>{alert.value}</strong></span>
                          <span>Ngưỡng: {alert.threshold}</span>
                        </div>
                      )}
                      {!rec && (
                        <Button size="sm" variant="ghost" onClick={() => getRecommendation(alert, idx)} disabled={isRecLoading}
                          className="ml-auto text-[10px] h-6 px-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 gap-1" data-testid={`ai-rec-btn-${idx}`}>
                          {isRecLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          {isRecLoading ? "Đang phân tích..." : "Gợi ý AI"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                {/* AI Recommendation panel */}
                {rec && (
                  <div className="mt-3 ml-5 bg-[#0f172a]/60 border border-cyan-500/20 rounded-lg p-3" data-testid={`ai-rec-panel-${idx}`}>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                      <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">Gợi ý hành động AI</span>
                    </div>
                    <div className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">{rec}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
